import { useUsersServiceUsersInviteAcceptCreate } from '@organization/api/queries'
import {
  LOGIN_URL,
  messages,
  PASSWORD_PATTERN
} from '@organization/app-constants'
import {
  AuthWrapper,
  OrganizationLogo
} from '@organization/features/authentication/components'
import { useSearchParams } from '@organization/hooks'
import { ValidationError } from '@organization/utils'
import { showApiErrors } from '@shared/utils'
import { Alert, App, Button, Card, Flex, Form, Input } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStyle } from '../components/AuthWrapper/styles'

export const InvitationForm = () => {
  const params = useSearchParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { message } = App.useApp()
  const { styles } = useStyle()
  const { t } = useTranslation()

  const token = params.get('token')

  useEffect(() => {
    form.setFieldsValue({ invite_token: token })
  }, [token])

  const { mutate, isPending } = useUsersServiceUsersInviteAcceptCreate({
    onError: (err) => {
      const error = err as ValidationError
      if (error instanceof ValidationError) {
        if (Array.isArray(error.fields)) {
          form.setFields(error.fields)
        }
      }

      showApiErrors(error, form)
    },
    onSuccess: () => {
      message.success(messages.successRegistration)
      navigate(LOGIN_URL)
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      requestBody: {
        first_name: values?.first_name,
        last_name: values?.last_name,
        password: values.password,
        invite_token: values.invite_token
      }
    })
  }

  return (
    <AuthWrapper>
      <Card className={styles.card}>
        <Flex vertical gap={16}>
          <OrganizationLogo
            message={(name) => `${t('auth.invitation_header')} ${name}`}
          />
          {!token && (
            <Alert
              type="error"
              message={t('error.invalid_invitation')}
              description={t('error.invitation_missing_token')}
              showIcon
            />
          )}
        </Flex>

        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          requiredMark={false}
        >
          <Form.Item name="invite_token" hidden>
            <Input autoComplete="off" />
          </Form.Item>

          <Form.Item
            label={t('form.item.last_name')}
            name="last_name"
            rules={[{ required: true, message: t('form.message.last_name') }]}
          >
            <Input
              disabled={!token}
              autoComplete="off"
              placeholder={t('form.message.last_name')}
            />
          </Form.Item>

          <Form.Item
            label={t('form.item.first_name')}
            name="first_name"
            rules={[{ required: true, message: t('form.message.first_name') }]}
          >
            <Input
              disabled={!token}
              autoComplete="off"
              placeholder={t('form.message.first_name')}
            />
          </Form.Item>

          <Form.Item
            label={t('form.item.password')}
            name="password"
            rules={[
              {
                pattern: PASSWORD_PATTERN,
                message: t('form.message.new_password_regex')
              },
              { required: true, message: t('form.message.password_required') }
            ]}
          >
            <Input.Password
              disabled={!token}
              autoComplete="off"
              placeholder={t('form.message.password')}
            />
          </Form.Item>

          <Button
            block
            type="primary"
            disabled={!token}
            htmlType="submit"
            loading={isPending}
            onClick={onSubmit}
          >
            {t('button.accept_invitation')}
          </Button>
        </Form>
      </Card>
    </AuthWrapper>
  )
}
